import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUI } from 'contexts/UIContext';
import { useInView } from 'react-intersection-observer';
import { requestApi } from 'utils/request';
import SearchBox from 'components/SearchBox';
import SearchCategorySection from './SearchCategorySection';
import { SearchBoxWrapper } from '../QnATab/style';
import { ServiceCenterContentsWrapper } from '../style';

const FAQTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const newParams = new URLSearchParams(searchParams);

  const { setContextLoading, setModalData } = useUI();

  // 카테고리 리스트 & 선택된 카테고리
  const [faqCategoryList, setFaqCategoryList] = useState([]);

  // 판매몰리스트 & 선택된 판매몰
  const [mallList, setMallList] = useState([]);
  const [selectedMallSeq, setSelectedMallSeq] = useState(0);

  // 질문 리스트
  const [faqList, setFaqList] = useState([]);

  // 검색어
  const [searchValue, setSearchValue] = useState(''); // search input value

  // 무한스크롤 여부 판단용 페이지 num 저장
  const [currentPage, setCurrentPage] = useState(1);
  const [endPage, setEndPage] = useState(2);

  // 카테고리 선택
  const selectedCategoryCd = searchParams.get('categoryCd');

  // 무한스크롤
  const [ref, inView] = useInView({
    threshold: 0,
  });

  // '자주 묻는 질문' 카테고리 불러오기
  const getFnQCategoryCode = async () => {
    try {
      const res = await requestApi({
        url: '/v1/na/faq/code',
        method: 'GET',
      });
      if (res.success) {
        const { list } = res?.data;
        setFaqCategoryList([
          {
            code: 'FAQ100',
            codeType: 'FAQ',
            codeName: '자주 찾는 질문',
            codeOrder: 1,
          },
          ...list,
        ]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // '데이터 연동' 카테고리 판매몰 리스트 불러오기
  const getMallList = async () => {
    try {
      const res = await requestApi({
        url: '/v1/na/faq/only-y/interlock/mall',
        method: 'GET',
        params: { dataProviderSeq: 10002 },
      });
      if (res.success) {
        const { list } = res?.data;
        setMallList([
          { label: '판매몰 전체', value: 0 },
          ...list.map(item => ({ value: item.mallSeq, label: item.mallName })),
        ]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // '자주 묻는 질문' 리스트 가져오기
  const getServiceFnqList = async (first, params) => {
    try {
      const res = await requestApi({
        url: '/v1/na/faq',
        method: 'GET',
        params: {
          pageNo: first ? 1 : currentPage + 1,
          ...params,
        },
      });
      if (res.success) {
        const { list, paging } = res?.data;
        setCurrentPage(paging?.pageNo);
        setEndPage(paging?.endPageNo);
        if (first) {
          setFaqList(list);
        } else {
          setFaqList(prev => [...prev, ...list]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setContextLoading(false);
    }
  };

  // 카테고리 버튼 클릭
  const onClickCategoryBtn = code => {
    if (!!code) {
      newParams.set('categoryCd', code);
      newParams.set('search', '');
    } else {
      newParams.set('categoryCd', '');
    }

    setSearchParams(newParams);
    setSearchValue(''); // 검색어 초기화
    setSelectedMallSeq(0);
    getServiceFnqList(true, {
      classificationCode: code,
    });
  };

  // '데이터 연동' 판매몰 변경
  const onChangeMall = event => {
    const { value } = event.target;
    setSelectedMallSeq(value);
    getServiceFnqList(true, {
      classificationCode: selectedCategoryCd,
      mallSeq: value,
    });
  };

  const onChangeSearchValue = event => {
    setSearchValue(event.target.value);
  };

  const onClickSearch = () => {
    if (!searchValue.trim()) {
      setSearchValue('');
      setModalData({
        contents: '검색어를 입력해주세요',
      });

      return;
    }

    setContextLoading(true);
    getServiceFnqList(true, { searchValue: searchValue });

    // 카테고리 셀렉 초기화
    newParams.set('categoryCd', '');
    newParams.set('search', searchValue);
    setSearchParams(newParams);
  };

  useEffect(() => {
    getFnQCategoryCode();
    getMallList();
  }, []);

  // 무한 스크롤 동작
  useEffect(() => {
    if (inView && currentPage < endPage)
      setTimeout(() => {
        getServiceFnqList(false, {
          ...(selectedCategoryCd && { classificationCode: selectedCategoryCd }),
          ...(searchValue && { searchValue }),
          ...(selectedMallSeq && { mallSeq: selectedMallSeq }),
        });
      }, 300);
  }, [inView]);

  useEffect(() => {
    if (!searchParams.has('categoryCd')) {
      newParams.set('categoryCd', 'FAQ100');
      setSearchParams(newParams);
    }
  }, []);

  const searchValueParams = searchParams.get('search');

  useEffect(() => {
    if (searchValueParams) {
      setSearchValue(searchValueParams);
      getServiceFnqList(true, { searchValue: searchValueParams });
    } else getServiceFnqList(true, { classificationCode: 'FAQ100' });
  }, []);

  return (
    <ServiceCenterContentsWrapper>
      <SearchBoxWrapper>
        <SearchBox
          placeholder="궁금하신 점을 빠르게 검색해 보세요."
          value={searchValue}
          onClickSearch={onClickSearch}
          onChange={onChangeSearchValue}
          maxLength={14}
        />
      </SearchBoxWrapper>

      {faqCategoryList.length > 0 && (
        <>
          <SearchCategorySection
            faqCategoryList={faqCategoryList}
            onClickCategoryBtn={onClickCategoryBtn}
            selectedCategoryCd={selectedCategoryCd}
            findValue={searchValueParams}
            faqList={faqList}
            mallList={mallList}
            onChangeMall={onChangeMall}
            selectedMallSeq={selectedMallSeq}
          />
          <div ref={ref} />
        </>
      )}
    </ServiceCenterContentsWrapper>
  );
};

export default FAQTab;
